/* ==========================================================================
   BANCO — Analyse de la photo client & préparation du détourage

   Le configurateur recevait toutes les images de la même façon : détourage IA
   puis conversion gravure avec des réglages fixes. Or un logo, un portrait, un
   chien et un paysage n'appellent pas du tout le même traitement — c'est de là
   que venaient les ratés (lettres rognées sur les logos, vêtements en masse
   noire, planche vide sur un paysage, monogramme doré invisible).

   Ce module répond à trois questions AVANT la gravure :
     1. à quoi ai-je affaire ?          → analyser(img)
     2. comment isoler le sujet ?       → extraireEncre(img) pour les aplats,
                                          IA + nettoyerMasque() pour les photos
     3. le détourage est-il exploitable ? → noterMasque(canvas)

   Zéro dépendance, Canvas 2D uniquement.
   ========================================================================== */
(function (global) {
  'use strict';

  var MINI = 260;   /* côté max de la miniature d'analyse — assez pour les
                       statistiques, assez petit pour rester sous 10 ms */
  var COMPO = 384;  /* côté max pour l'étiquetage des îlots du masque */

  /* ------------------------------------------------------------------ outils */

  function toCanvas(image, maxSide) {
    var w = image.naturalWidth || image.width;
    var h = image.naturalHeight || image.height;
    var s = maxSide ? Math.min(1, maxSide / Math.max(w, h)) : 1;
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w * s));
    c.height = Math.max(1, Math.round(h * s));
    var x = c.getContext('2d', { willReadFrequently: true });
    x.imageSmoothingEnabled = true;
    x.imageSmoothingQuality = 'high';
    x.drawImage(image, 0, 0, c.width, c.height);
    return c;
  }

  function data(canvas) {
    return canvas.getContext('2d', { willReadFrequently: true })
      .getImageData(0, 0, canvas.width, canvas.height);
  }

  function lisser(bas, haut, v) {
    if (haut === bas) return v >= haut ? 1 : 0;
    var t = (v - bas) / (haut - bas);
    if (t < 0) t = 0; else if (t > 1) t = 1;
    return t * t * (3 - 2 * t);
  }

  /* Flou par sommes courantes, séparable : O(n) quel que soit le rayon.
     Sert au contraste local et à l'adoucissement des bords de masque. */
  function flouBoite(src, w, h, r) {
    if (r < 1) return src;
    var tmp = new Float32Array(w * h);
    var out = new Float32Array(w * h);
    var x, y, i, somme, n;
    for (y = 0; y < h; y++) {
      somme = 0; n = 0;
      for (i = 0; i <= r && i < w; i++) { somme += src[y * w + i]; n++; }
      for (x = 0; x < w; x++) {
        tmp[y * w + x] = somme / n;
        var sortant = x - r, entrant = x + r + 1;
        if (sortant >= 0) { somme -= src[y * w + sortant]; n--; }
        if (entrant < w) { somme += src[y * w + entrant]; n++; }
      }
    }
    for (x = 0; x < w; x++) {
      somme = 0; n = 0;
      for (i = 0; i <= r && i < h; i++) { somme += tmp[i * w + x]; n++; }
      for (y = 0; y < h; y++) {
        out[y * w + x] = somme / n;
        var s2 = y - r, e2 = y + r + 1;
        if (s2 >= 0) { somme -= tmp[s2 * w + x]; n--; }
        if (e2 < h) { somme += tmp[e2 * w + x]; n++; }
      }
    }
    return out;
  }

  /* Filtre guidé (He, Sun & Tang) : recolle le masque sur les contours RÉELS de
     la photo. C'est la pièce maîtresse du détourage des portraits.

     Le modèle IA raisonne sur une image de 1024 px et rend un contour lisse,
     « au ciseau ». Les cheveux, les poils, la frange d'un pull n'y survivent
     pas : ils deviennent un casque. Le filtre guidé reprend ce masque grossier
     et, dans chaque petite fenêtre, cherche la meilleure fonction affine du
     guide (la photo d'origine) qui l'approche. Là où la photo a un vrai bord,
     le masque en hérite ; là où elle est unie, il reste lisse.

     Six flous par sommes courantes, donc O(n) — quelques dizaines de ms.
     `eps` fixe la finesse : petit = colle aux moindres mèches (et au bruit),
     grand = reste sage. */
  function filtreGuide(guide, entree, w, h, r, eps) {
    var n = w * h, i;
    var gp = new Float32Array(n), gg = new Float32Array(n);
    for (i = 0; i < n; i++) { gp[i] = guide[i] * entree[i]; gg[i] = guide[i] * guide[i]; }
    var mG = flouBoite(guide, w, h, r);
    var mP = flouBoite(entree, w, h, r);
    var mGP = flouBoite(gp, w, h, r);
    var mGG = flouBoite(gg, w, h, r);
    var A = new Float32Array(n), B = new Float32Array(n);
    for (i = 0; i < n; i++) {
      var vari = mGG[i] - mG[i] * mG[i];
      var cov = mGP[i] - mG[i] * mP[i];
      var a = cov / (vari + eps);
      A[i] = a;
      B[i] = mP[i] - a * mG[i];
    }
    var mA = flouBoite(A, w, h, r);
    var mB = flouBoite(B, w, h, r);
    var out = new Float32Array(n);
    for (i = 0; i < n; i++) {
      var v = mA[i] * guide[i] + mB[i];
      out[i] = v < 0 ? 0 : v > 1 ? 1 : v;
    }
    return out;
  }

  /* Luminance d'une image redimensionnée à w × h, en [0,1]. Sert de guide. */
  function luminance(image, w, h) {
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var x = c.getContext('2d', { willReadFrequently: true });
    x.imageSmoothingEnabled = true;
    x.imageSmoothingQuality = 'high';
    x.drawImage(image, 0, 0, w, h);
    var px = x.getImageData(0, 0, w, h).data;
    var g = new Float32Array(w * h);
    for (var i = 0, p = 0; i < px.length; i += 4, p++) {
      g[p] = (0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2]) / 255;
    }
    return g;
  }

  /* Minimum (érosion) ou maximum (dilatation) sur une fenêtre carrée, en deux
     passes séparables : O(n × r) au lieu de O(n × r²). Modifie `src` sur place. */
  function morpho(src, w, h, r, erode) {
    if (r < 1) return;
    var tmp = new Float32Array(w * h);
    var x, y, i, k, v, best;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        best = src[y * w + x];
        for (k = -r; k <= r; k++) {
          i = x + k;
          if (i < 0 || i >= w) continue;
          v = src[y * w + i];
          if (erode ? v < best : v > best) best = v;
        }
        tmp[y * w + x] = best;
      }
    }
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        best = tmp[y * w + x];
        for (k = -r; k <= r; k++) {
          i = y + k;
          if (i < 0 || i >= h) continue;
          v = tmp[i * w + x];
          if (erode ? v < best : v > best) best = v;
        }
        src[y * w + x] = best;
      }
    }
  }

  /* --------------------------------------------------------------- analyse */

  /* Statistiques à la résolution NATIVE, sur deux fenêtres de 320 px (centre +
     coin haut-gauche). Réduire l'image d'abord aurait tout gâché : c'est
     justement la finesse du pixel qui sépare un logo d'une photo.

     « identiques » = proportion de pixels rigoureusement égaux à leur voisin
     de droite. Un aplat vectoriel en est plein, une photo n'en a jamais :
     même un ciel lisse porte du bruit de capteur et du bruit JPEG. */
  function statsNatives(image) {
    var w = image.naturalWidth || image.width;
    var h = image.naturalHeight || image.height;
    var side = Math.min(320, w, h);
    var fenetres = [[Math.floor((w - side) / 2), Math.floor((h - side) / 2)]];
    if (w > side * 1.6 || h > side * 1.6) fenetres.push([0, 0]);

    var c = document.createElement('canvas');
    c.width = side; c.height = side;
    var x = c.getContext('2d', { willReadFrequently: true });
    x.imageSmoothingEnabled = false;

    var paires = 0, identiques = 0;
    var seen = new Uint8Array(32768), couleurs = 0;

    fenetres.forEach(function (f) {
      x.clearRect(0, 0, side, side);
      x.drawImage(image, f[0], f[1], side, side, 0, 0, side, side);
      var d = x.getImageData(0, 0, side, side).data;
      for (var y = 0; y < side; y++) {
        for (var i = 0; i < side - 1; i++) {
          var p = (y * side + i) * 4;
          if (d[p + 3] < 24 || d[p + 7] < 24) continue;
          paires++;
          if (d[p] === d[p + 4] && d[p + 1] === d[p + 5] && d[p + 2] === d[p + 6]) identiques++;
          var idx = ((d[p] >> 3) << 10) | ((d[p + 1] >> 3) << 5) | (d[p + 2] >> 3);
          if (!seen[idx]) { seen[idx] = 1; couleurs++; }
        }
      }
    });
    return {
      identiques: paires ? identiques / paires : 0,
      couleursNatives: couleurs
    };
  }

  /* PART EXPLIQUÉE PAR UNE PALETTE — la mesure qui distingue vraiment un logo
     d'une photo, là où toutes les autres se trompent.

     Le piège : une photo sur fond uni ressemble beaucoup à un logo si l'on ne
     regarde que « combien de couleurs » et « quelle est la couleur dominante ».
     Un chat gris sur fond noir de studio affichait 50 % de dominance et
     667 couleurs — exactement le profil d'un logo. Il partait donc en découpe
     d'aplat et ressortait en une bouillie noir et blanc.

     Le vrai départage : un dessin s'explique ENTIÈREMENT par une petite
     palette — le fond, deux ou trois encres, et les fondus d'anti-crénelage
     entre chaque encre et le fond. On teste donc exactement cela : chaque
     pixel est-il sur l'un des segments [fond → encre] ? Un logo répond oui à
     plus de 90 % ; une photo, dont les tons filent dans toutes les directions
     de l'espace couleur, n'y arrive jamais.

     Une note, pas un verdict : c'est `analyser` qui décide ensuite. */
  function partExpliquee(px, fond, seaux, nOpaque, w, h) {
    if (!nOpaque) return { explique: 0, mitan: 0, mitanEpais: 0 };
    var i, k;
    /* encres candidates : seaux bien peuplés, loin du fond et distincts entre
       eux (sinon on prend trois nuances du même aplat) */
    var mini = nOpaque * 0.004;
    var cands = [];
    for (i = 0; i < seaux.length; i++) {
      if (seaux[i] < mini) continue;
      var cr = (((i >> 10) & 31) << 3) + 4;
      var cv = (((i >> 5) & 31) << 3) + 4;
      var cb = ((i & 31) << 3) + 4;
      var dFond = (Math.abs(cr - fond[0]) + Math.abs(cv - fond[1]) + Math.abs(cb - fond[2])) / (3 * 255);
      if (dFond < 0.06) continue;
      cands.push({ c: [cr, cv, cb], n: seaux[i], d: dFond });
    }
    cands.sort(function (a, b) { return b.n - a.n; });
    var encres = [];
    for (i = 0; i < cands.length && encres.length < 4; i++) {
      var pris = false;
      for (k = 0; k < encres.length; k++) {
        var e = encres[k];
        if ((Math.abs(e[0] - cands[i].c[0]) + Math.abs(e[1] - cands[i].c[1]) +
             Math.abs(e[2] - cands[i].c[2])) / (3 * 255) < 0.12) { pris = true; break; }
      }
      if (!pris) encres.push(cands[i].c);
    }
    if (!encres.length) return { explique: 1, mitan: 0, mitanEpais: 0 };

    /* distance au segment [fond → encre], en unités RGB de 0 à 1 */
    var TOL = 0.055, TOL2 = TOL * TOL * 3;
    var explique = 0, mitan = 0;
    var carteMitan = new Uint8Array(w * h);
    for (i = 0; i < px.length; i += 4) {
      if (px[i + 3] < 24) continue;
      var pr = px[i], pv = px[i + 1], pb = px[i + 2];
      var meilleur = Infinity, tMeilleur = 0;
      for (k = 0; k < encres.length; k++) {
        var ex = encres[k][0] - fond[0], ey = encres[k][1] - fond[1], ez = encres[k][2] - fond[2];
        var vx = pr - fond[0], vy = pv - fond[1], vz = pb - fond[2];
        var len2 = ex * ex + ey * ey + ez * ez;
        var t = len2 ? (vx * ex + vy * ey + vz * ez) / len2 : 0;
        if (t < 0) t = 0; else if (t > 1) t = 1;
        var dx = vx - t * ex, dy = vy - t * ey, dz = vz - t * ez;
        var d2 = (dx * dx + dy * dy + dz * dz) / (255 * 255);
        if (d2 < meilleur) { meilleur = d2; tMeilleur = t; }
      }
      if (meilleur <= TOL2) {
        explique++;
        /* Pixel à MI-CHEMIN entre le fond et une encre. Dans un dessin, il n'y
           en a qu'un filet : le liseré d'anti-crénelage au bord des traits.
           Dans une photo, c'est la matière même du sujet — et c'est le seul
           garde-fou contre le piège du fond noir ou blanc, où le segment
           [noir → blanc] traverse TOUS les gris et « explique » n'importe
           quelle photo en noir et blanc. */
        if (tMeilleur > 0.15 && tMeilleur < 0.85) { mitan++; carteMitan[i >> 2] = 1; }
      }
    }

    /* ÉPAISSEUR des demi-teintes — le dernier départage, et le plus sûr.

       Un liseré d'anti-crénelage fait un ou deux pixels de large : il ne
       survit pas à une érosion. La fourrure d'un chat, la peau d'un visage,
       un ciel dégradé forment au contraire de larges plaques qui, elles,
       survivent. On ne compte donc pas les demi-teintes, on regarde si elles
       tiennent debout une fois rabotées d'un pixel.

       C'est ce qui laisse passer un logo au trait fin — dont presque TOUS les
       pixels sont de l'anti-crénelage — sans laisser passer une photo en noir
       et blanc sur fond blanc, qui lui ressemblait sur tous les autres
       critères. */
    var epais = 0, x2, y2;
    for (y2 = 1; y2 < h - 1; y2++) {
      for (x2 = 1; x2 < w - 1; x2++) {
        var q = y2 * w + x2;
        if (!carteMitan[q]) continue;
        if (carteMitan[q - 1] && carteMitan[q + 1] &&
            carteMitan[q - w] && carteMitan[q + w]) epais++;
      }
    }
    return {
      explique: explique / nOpaque,
      mitan: mitan / nOpaque,
      mitanEpais: epais / nOpaque
    };
  }

  /* Renvoie un rapport complet sur l'image : nature, fond, statistiques
     tonales et réglages de gravure conseillés. */
  function analyser(image) {
    var c = toCanvas(image, MINI);
    var d = data(c), px = d.data, w = c.width, h = c.height;
    var n = w * h;

    var gris = new Float32Array(n);
    var alpha = new Float32Array(n);
    var histo = new Uint32Array(256);
    var seaux = new Uint32Array(32768); /* 5 bits par canal */
    var i, p, r, v, b, a, l, mx, mn;
    var nOpaque = 0, satTotal = 0, peau = 0, extremes = 0, nbCouleurs = 0;
    var seauDominant = -1, popDominante = 0;
    var moyR = 0, moyV = 0, moyB = 0;

    for (i = 0, p = 0; i < px.length; i += 4, p++) {
      r = px[i]; v = px[i + 1]; b = px[i + 2]; a = px[i + 3];
      alpha[p] = a / 255;
      l = (0.2126 * r + 0.7152 * v + 0.0722 * b) / 255;
      gris[p] = l;
      if (a < 24) continue;
      nOpaque++;
      moyR += r; moyV += v; moyB += b;
      histo[(l * 255 + 0.5) | 0]++;
      mx = r > v ? (r > b ? r : b) : (v > b ? v : b);
      mn = r < v ? (r < b ? r : b) : (v < b ? v : b);
      satTotal += mx ? (mx - mn) / mx : 0;
      if (l < 0.06 || l > 0.94) extremes++;
      var idx = ((r >> 3) << 10) | ((v >> 3) << 5) | (b >> 3);
      if (!seaux[idx]) nbCouleurs++;
      seaux[idx]++;
      if (seaux[idx] > popDominante) { popDominante = seaux[idx]; seauDominant = idx; }
      /* peau : règle YCbCr classique, robuste sur toutes les carnations */
      var cb = 128 - 0.168736 * r - 0.331264 * v + 0.5 * b;
      var cr = 128 + 0.5 * r - 0.418688 * v - 0.081312 * b;
      if (cb >= 77 && cb <= 133 && cr >= 133 && cr <= 180 && l > 0.15 && mx - mn > 8) peau++;
    }
    if (!nOpaque) nOpaque = 1;
    moyR /= nOpaque; moyV /= nOpaque; moyB /= nOpaque;

    /* Aplat : proportion de pixels dont le voisinage 3×3 est uniforme.
       Un logo vectoriel est massivement plat, une photo ne l'est jamais. */
    var plats = 0, contours = 0, compte = 0;
    for (var y = 1; y < h - 1; y++) {
      for (var x = 1; x < w - 1; x++) {
        p = y * w + x;
        if (alpha[p] < 0.1) continue;
        var g0 = gris[p];
        var haut = gris[p - w], bas = gris[p + w], gau = gris[p - 1], dro = gris[p + 1];
        var ecart = Math.max(Math.abs(g0 - haut), Math.abs(g0 - bas),
                             Math.abs(g0 - gau), Math.abs(g0 - dro));
        compte++;
        if (ecart < 0.02) plats++;
        if (ecart > 0.10) contours++;
      }
    }
    if (!compte) compte = 1;

    /* Fond : anneau des 6 % de bord. S'il est uniforme, on peut détourer à la
       couleur — bien plus fidèle que l'IA sur un logo. */
    var bord = Math.max(2, Math.round(Math.min(w, h) * 0.06));
    var sR = 0, sV = 0, sB = 0, sA = 0, nb = 0;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        if (x >= bord && x < w - bord && y >= bord && y < h - bord) continue;
        i = (y * w + x) * 4;
        sA += px[i + 3];
        if (px[i + 3] < 24) { nb++; continue; }
        sR += px[i]; sV += px[i + 1]; sB += px[i + 2];
        nb++;
      }
    }
    /* Un PNG de logo est souvent rogné au plus près : son anneau de bord
       traverse le dessin et paraît opaque. C'est la part de transparence sur
       TOUTE l'image qui dit qu'un canal alpha sert de fond. */
    var alphaBord = sA / (nb * 255);
    var fondTransparent = (1 - nOpaque / n) > 0.06 || alphaBord < 0.12;

    /* Couleur de fond = teinte DOMINANTE de l'image, pas la moyenne du bord.
       Un logo est souvent rogné au plus près : le bord traverse alors le
       dessin (la pointe noire du losange BANCO), et la moyenne du bord donnait
       un gris qui n'existe nulle part. La teinte dominante, elle, est le
       fond — c'est ce qui couvre le plus de surface. */
    var fond = [255, 255, 255];
    if (seauDominant >= 0) {
      var qr = (seauDominant >> 10) & 31, qv = (seauDominant >> 5) & 31, qb = seauDominant & 31;
      /* moyenne fine des pixels du seau : on récupère la vraie couleur */
      var fR = 0, fV = 0, fB = 0, nf = 0;
      for (i = 0; i < px.length; i += 4) {
        if (px[i + 3] < 24) continue;
        if ((px[i] >> 3) !== qr || (px[i + 1] >> 3) !== qv || (px[i + 2] >> 3) !== qb) continue;
        fR += px[i]; fV += px[i + 1]; fB += px[i + 2]; nf++;
      }
      if (nf) fond = [fR / nf, fV / nf, fB / nf];
    }
    var dominance = nOpaque ? popDominante / nOpaque : 0;

    /* Le pourtour appartient-il au fond ? On compte la PART du pourtour qui est
       vraiment de la couleur dominante, au lieu de moyenner son écart.
       La moyenne se faisait piéger par tous les logos rognés au plus près :
       le cadre noir de « GIANT » ou le filet bleu de « THE BOSTON BEER
       COMPANY » traversent l'anneau de bord, l'écart moyen explosait à 60-97
       et ces logos partaient à l'IA alors que leur fond est un blanc parfait. */
    var ecartBord = 0, nEcart = 0, bordFond = 0;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        if (x >= bord && x < w - bord && y >= bord && y < h - bord) continue;
        i = (y * w + x) * 4;
        if (px[i + 3] < 24) continue;
        var eb = Math.abs(px[i] - fond[0]) + Math.abs(px[i + 1] - fond[1]) + Math.abs(px[i + 2] - fond[2]);
        ecartBord += eb;
        if (eb / 3 < 26) bordFond++;
        nEcart++;
      }
    }
    bordFond = nEcart ? bordFond / nEcart : 0;
    ecartBord = nEcart ? ecartBord / (nEcart * 3) : 0;

    /* Proportion de l'image qui se distingue du fond : dit si un détourage à
       la couleur laisserait quelque chose de sensé. */
    var horsFond = 0;
    for (i = 0, p = 0; i < px.length; i += 4, p++) {
      if (px[i + 3] < 24) continue;
      var dd = (Math.abs(px[i] - fond[0]) + Math.abs(px[i + 1] - fond[1]) + Math.abs(px[i + 2] - fond[2])) / 3;
      if (dd > 26) horsFond++;
    }

    var nat = statsNatives(image);
    var explique = partExpliquee(px, fond, seaux, nOpaque, w, h);

    var m = {
      largeur: image.naturalWidth || image.width,
      hauteur: image.naturalHeight || image.height,
      aplat: plats / compte,
      contours: contours / compte,
      couleurs: nbCouleurs,
      couleursNatives: nat.couleursNatives,
      identiques: nat.identiques,
      dominance: dominance,
      extremes: extremes / nOpaque,
      saturation: satTotal / nOpaque,
      peau: peau / nOpaque,
      fondTransparent: fondTransparent,
      fondCouleur: [Math.round(fond[0]), Math.round(fond[1]), Math.round(fond[2])],
      fondEcart: ecartBord,
      bordFond: bordFond,
      explique: explique.explique,
      mitan: explique.mitan,
      mitanEpais: explique.mitanEpais,
      fondClair: (0.2126 * fond[0] + 0.7152 * fond[1] + 0.0722 * fond[2]) / 255,
      horsFond: horsFond / nOpaque,
      opacite: nOpaque / n
    };

    /* --- nature de l'image ----------------------------------------------
       On ne cherche PAS à deviner le sujet (« un chien », « une mariée ») :
       on mesure ce qui change réellement le traitement.

       « trait » : logo, dessin, texte — des aplats parfaits, très peu de
       couleurs, et surtout des pixels rigoureusement identiques à leur voisin,
       ce qu'aucune photo ne produit jamais.
       « photo » : tout le reste. La distinction portrait / animal / paysage
       est ensuite gérée par les statistiques (carnation, densité d'encre),
       pas par une étiquette hasardeuse.

       Mesuré sur les douze cas d'essai : les logos tiennent tous au-dessus de
       0,80 de pixels identiques et sous 500 couleurs natives ; la photo la
       plus « plate » (un homme en costume noir sur fond sombre) plafonne à
       0,62 et 311 couleurs. Les deux axes ensemble laissent une marge nette. */
    /* La dominance seule ne suffit PAS : un studio noir ou un mur blanc la font
       monter autant qu'un fond de logo. On exige donc que l'image s'explique
       par une petite palette (cf. partExpliquee). Mesuré sur le corpus d'essai
       de 138 images : les logos tiennent au-dessus de 0,90, les photos à fond
       uni les plus trompeuses — le chat gris sur noir, le portrait au capuchon
       sur noir — restent sous 0,80. */
    var estTrait = m.couleursNatives < 900 && m.explique > 0.88 && m.mitanEpais < 0.045 &&
                   (m.identiques > 0.55 || m.dominance > 0.30 || m.aplat > 0.60);
    m.nature = estTrait ? 'trait' : 'photo';
    /* carnation : peau, poil fauve, rouges… tout ce qui vire au noir avec une
       luminance Rec.709 et redevient lisible avec un filtre orange */
    m.carnation = !estTrait && (m.peau > 0.15 || (moyR > moyV * 1.10 && moyV > moyB * 1.06));

    /* Fond détourable à la couleur : dominant, présent tout autour, et le sujet
       en ressort. Le plancher sur `horsFond` est très bas : un logo-signature
       en lettres fines ne couvre que quelques pour mille de l'image, et à
       0,015 il partait à l'IA — qui le noyait dans un halo. */
    m.fondUni = fondTransparent ||
      (m.dominance > 0.20 && m.bordFond > 0.30 && m.horsFond > 0.003 && m.horsFond < 0.94);
    m.detourage = (estTrait && m.fondUni) ? 'aplat' : 'ia';

    m.gravure = reglages(m);
    return m;
  }

  /* Réglages du moteur de gravure déduits de l'analyse. C'est ici que se joue
     l'essentiel du réalisme : un portrait ne se grave pas comme un logo. */
  function reglages(m) {
    if (m.nature === 'trait') {
      return {
        mode: 'trait',        /* pas d'auto-niveaux, pas de courbe : l'encre est déjà binaire */
        lumaMix: null,
        autoNiveaux: false,
        contrasteLocal: 0,
        netiquete: 0,
        encreCible: 0,        /* 0 = pas d'auto-exposition */
        knee: 0.04,
        gamma: 0.75
      };
    }
    if (m.carnation) {
      return {
        mode: 'photo',
        /* filtre orange du noir & blanc argentique : la peau s'éclaircit, les
           rouges cessent de virer au noir (pull bordeaux = masse noire avant) */
        lumaMix: [0.34, 0.50, 0.16],
        autoNiveaux: true,
        clip: 0.002,
        plancher: 0.045,      /* on ne descend pas à zéro : le noir bouché tue le visage */
        contrasteLocal: 0.55, /* fait ressortir les plis d'un vêtement sombre */
        rayonLocal: 0.055,
        netiquete: 0.35,
        encreCible: 0.38,     /* densité d'encre visée sur le sujet */
        knee: 0.24,
        gamma: 0.92
      };
    }
    return {
      mode: 'photo',
      lumaMix: null,          /* Rec.709 */
      autoNiveaux: true,
      clip: 0.005,
      plancher: 0.02,
      contrasteLocal: 0.55,   /* poils, textures : c'est le contraste local qui les révèle */
      rayonLocal: 0.05,
      netiquete: 0.45,
      encreCible: 0.36,
      knee: 0.26,
      gamma: 0.85
    };
  }

  /* ------------------------------------------- détourage des aplats (logos) */

  /* Extrait « l'encre » d'un logo : tout ce qui se distingue du fond. Le
     résultat est déjà une carte de gravure (noir = brûlé, alpha = couverture).

     Pourquoi pas l'IA : sur un logo, isnet rogne les lettres qui touchent le
     bord (le R de « COMPTOIR » disparaissait) et laisse un voile gris dans les
     contre-formes. Ici la découpe est exacte, l'anti-crénelage est conservé, et
     un monogramme doré sur fond marine grave enfin en positif. */
  /* Centile d'une série de valeurs [0,1] passée par un histogramme de 256 cases. */
  function centile(histo, total, part) {
    if (!total) return 0;
    var cible = total * part, cumul = 0;
    for (var k = 0; k < 256; k++) {
      cumul += histo[k];
      if (cumul >= cible) return k / 255;
    }
    return 1;
  }

  function extraireEncre(image, m) {
    m = m || analyser(image);
    var c = toCanvas(image, Math.min(2000, Math.max(image.naturalWidth || image.width, 900)));
    var d = data(c), px = d.data;
    var fond = m.fondCouleur;
    var transparent = m.fondTransparent;
    var i, p, a, dist, encre, l;

    /* --- On mesure d'abord l'échelle propre à CE logo -----------------------
       Les seuils fixes d'avant supposaient un logo bien contrasté. Deux cas
       très courants leur échappaient :
         · un logo pâle (gris clair sur blanc, doré sur crème) n'atteignait
           jamais le seuil haut et sortait à moitié transparent, donc à peine
           gravé ;
         · un logo tout en teintes claires posé sur de la transparence (un
           monogramme jaune et orange) tombait sous le seuil de « sombre » :
           ses aplats jaunes disparaissaient purement et simplement.
       On calcule donc l'étendue réelle du logo et on s'y adapte. */
    var histoD = new Uint32Array(256);   /* écart au fond, sur fond opaque */
    var histoL = new Uint32Array(256);   /* luminance, sur fond transparent */
    var nOpaque = 0;
    for (i = 0; i < px.length; i += 4) {
      if (px[i + 3] < 128) continue;
      nOpaque++;
      if (transparent) {
        l = (0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2]) / 255;
        histoL[(l * 255 + 0.5) | 0]++;
      } else {
        dist = (Math.abs(px[i] - fond[0]) + Math.abs(px[i + 1] - fond[1]) +
                Math.abs(px[i + 2] - fond[2])) / (3 * 255);
        histoD[Math.min(255, (dist * 255 + 0.5) | 0)]++;
      }
    }

    /* fond transparent : le dessin est-il un aplat unique (silhouette) ou
       porte-t-il des valeurs à respecter (un blason bicolore) ? */
    var silhouette = false, lBas = 0, lHaut = 1;
    if (transparent) {
      lBas = centile(histoL, nOpaque, 0.04);
      lHaut = centile(histoL, nOpaque, 0.96);
      /* moins de 0,22 d'étendue = une seule valeur : on grave la forme entière,
         quelle que soit sa couleur. C'est ce qui sauve les logos clairs. */
      silhouette = !nOpaque || (lHaut - lBas) < 0.22;
    }

    /* fond opaque : à quelle distance du fond se situe vraiment le tracé ?
       Le haut de l'échelle devient le noir de gravure, quitte à ce qu'il vaille
       0,12 sur un logo pâle. */
    var dHaut = 0.30, dBas = 0.055;
    if (!transparent) {
      var d96 = centile(histoD, nOpaque, 0.985);
      if (d96 > 0.02) {
        dHaut = Math.min(0.30, d96 * 0.75);
        dBas = Math.max(0.012, dHaut * 0.18);
      }
    }

    for (i = 0, p = 0; i < px.length; i += 4, p++) {
      a = px[i + 3] / 255;
      if (a < 0.02) { px[i] = 255; px[i + 1] = 255; px[i + 2] = 255; px[i + 3] = 0; continue; }
      if (transparent) {
        if (silhouette) {
          encre = a;
        } else {
          l = (0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2]) / 255;
          /* darkness relative À CE LOGO : le plus clair du dessin reste visible
             (encre faible mais non nulle), le plus foncé sature */
          encre = a * (0.22 + 0.78 * lisser(lBas, lHaut, lHaut - (l - lBas)));
        }
      } else {
        dist = (Math.abs(px[i] - fond[0]) + Math.abs(px[i + 1] - fond[1]) + Math.abs(px[i + 2] - fond[2])) / (3 * 255);
        /* seuil bas : l'anti-crénelage des lettres tombe dans la pente douce,
           les traits pleins saturent tout de suite */
        encre = a * lisser(dBas, dHaut, dist);
      }
      var gris = Math.round(255 * (1 - encre));
      px[i] = gris; px[i + 1] = gris; px[i + 2] = gris;
      px[i + 3] = Math.round(255 * Math.min(1, encre * 1.25));
    }
    c.getContext('2d').putImageData(d, 0, 0);
    return recadrer(c, 0.012);
  }

  /* ------------------------------------------------- nettoyage du masque IA */

  /* Étiquetage des îlots sur une version réduite du masque : les miettes de
     décor que l'IA laisse autour des cheveux ou sous les pattes disparaissent,
     les vraies silhouettes (une famille = 20 composantes) sont conservées. */
  function carteIlots(alphaPetit, w, h) {
    var n = w * h;
    var label = new Int32Array(n).fill(-1);
    var pile = new Int32Array(n);
    var aires = [];
    var courant = 0, i, tete, p, x, y;
    for (i = 0; i < n; i++) {
      if (alphaPetit[i] < 0.5 || label[i] !== -1) continue;
      tete = 0; pile[tete++] = i; label[i] = courant;
      var aire = 0;
      while (tete > 0) {
        p = pile[--tete];
        aire++;
        x = p % w; y = (p / w) | 0;
        if (x > 0 && alphaPetit[p - 1] >= 0.5 && label[p - 1] === -1) { label[p - 1] = courant; pile[tete++] = p - 1; }
        if (x < w - 1 && alphaPetit[p + 1] >= 0.5 && label[p + 1] === -1) { label[p + 1] = courant; pile[tete++] = p + 1; }
        if (y > 0 && alphaPetit[p - w] >= 0.5 && label[p - w] === -1) { label[p - w] = courant; pile[tete++] = p - w; }
        if (y < h - 1 && alphaPetit[p + w] >= 0.5 && label[p + w] === -1) { label[p + w] = courant; pile[tete++] = p + w; }
      }
      aires.push(aire);
      courant++;
    }
    return { label: label, aires: aires };
  }

  /* Note la qualité d'un détourage IA. Sert à refuser un masque inexploitable
     — un paysage sans sujet rendait la planche VIDE, avec trois poussières. */
  function noterMasque(image) {
    var c = toCanvas(image, COMPO);
    var d = data(c), px = d.data, w = c.width, h = c.height, n = w * h;
    var petit = new Float32Array(n);
    var vus = 0, francs = 0, i, p;
    for (i = 0, p = 0; i < px.length; i += 4, p++) {
      var a = px[i + 3] / 255;
      petit[p] = a;
      if (a > 0.15) vus++;
      if (a > 0.85) francs++;
    }
    var ilots = carteIlots(petit, w, h);
    var total = 0, maxi = 0, gros = 0;
    ilots.aires.forEach(function (a) {
      total += a;
      if (a > maxi) maxi = a;
    });
    ilots.aires.forEach(function (a) { if (a >= total * 0.006) gros++; });

    var couverture = total / n;
    var nettete = vus ? francs / vus : 0;
    var partDuPlusGrand = total ? maxi / total : 0;

    /* CE QU'EST UN MASQUE INEXPLOITABLE : de la poussière. Trois grains de
       décor sur un paysage, qui donnaient une planche vide.

       L'ancien test exigeait aussi que le masque soit « franc » (part de
       l'alpha au-dessus de 0,85). C'était l'exact contraire de ce qu'il fallait :
       la franchise se mesure ici sur une réduction à 384 px, où toute finesse
       devient du demi-ton. Plus le sujet était détaillé — un chat à poil long,
       une famille, une foule de figurines — plus sa note tombait et plus il
       était rejeté. Sur le corpus d'essai, 22 photos sur 138 se retrouvaient
       gravées avec tout leur décor, dont des masques parfaits couvrant 40 % de
       l'image d'un seul tenant. C'était le premier défaut du détourage.

       On juge donc sur ce qui sépare vraiment la poussière d'un sujet : la
       surface couverte, et le fait qu'une silhouette domine l'ensemble. */
    var exploitable = couverture >= 0.018 &&
                      (partDuPlusGrand >= 0.35 || nettete >= 0.55);
    return {
      couverture: couverture,
      nettete: nettete,
      fragments: ilots.aires.length,
      fragmentsUtiles: gros,
      partDuPlusGrand: partDuPlusGrand,
      exploitable: exploitable
    };
  }

  /* Distance (approchée, chanfrein 3-4 en deux passes) à la zone marquée dans
     `graine`. Sert à mesurer l'éloignement au « noyau sûr » du masque. */
  function distanceAuNoyau(graine, w, h) {
    var INF = 1e9;
    var d = new Float32Array(w * h);
    var i, x, y, p, v;
    for (i = 0; i < d.length; i++) d[i] = graine[i] ? 0 : INF;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        p = y * w + x;
        v = d[p];
        if (x > 0 && d[p - 1] + 3 < v) v = d[p - 1] + 3;
        if (y > 0 && d[p - w] + 3 < v) v = d[p - w] + 3;
        if (x > 0 && y > 0 && d[p - w - 1] + 4 < v) v = d[p - w - 1] + 4;
        if (x < w - 1 && y > 0 && d[p - w + 1] + 4 < v) v = d[p - w + 1] + 4;
        d[p] = v;
      }
    }
    for (y = h - 1; y >= 0; y--) {
      for (x = w - 1; x >= 0; x--) {
        p = y * w + x;
        v = d[p];
        if (x < w - 1 && d[p + 1] + 3 < v) v = d[p + 1] + 3;
        if (y < h - 1 && d[p + w] + 3 < v) v = d[p + w] + 3;
        if (x < w - 1 && y < h - 1 && d[p + w + 1] + 4 < v) v = d[p + w + 1] + 4;
        if (x > 0 && y < h - 1 && d[p + w - 1] + 4 < v) v = d[p + w - 1] + 4;
        d[p] = v;
      }
    }
    for (i = 0; i < d.length; i++) d[i] /= 3; /* retour en pixels */
    return d;
  }

  /* Nettoyage du masque IA — deux questions séparées.

     L'ancienne version mélangeait les deux et le payait cher : pour chasser un
     buisson accroché aux jambes, elle durcissait TOUT l'alpha (seuil 0,30) puis
     érodait toute la silhouette. Les cheveux, les poils et les mèches, qui
     vivent précisément dans l'alpha faible, disparaissaient avec le buisson :
     d'où le contour « casque » qui trahissait le simulateur.

     On traite donc séparément :

       QUOI garder  → un portillon (`gate`) calculé en basse résolution sur une
                      copie durcie du masque : îlots parasites, plaques
                      hallucinées loin du sujet. Destructif, mais il ne décide
                      que de zones, jamais du tracé.
       OÙ est le bord → le filtre guidé recolle l'alpha sur les contours réels
                      de la photo d'origine. Non destructif, il RAJOUTE du
                      détail que l'IA avait lissé.

     Final = détail × portillon. Les mèches reviennent, le buisson reste dehors.

     options.guide : la photo d'origine (indispensable au filtre guidé — les
     couleurs de la sortie IA sont effacées hors du sujet, elles ne peuvent pas
     servir de repère). */
  function nettoyerMasque(image, options) {
    options = options || {};
    var c = toCanvas(image, options.maxSide || 1600);
    var d = data(c), px = d.data, w = c.width, h = c.height, n = w * h;

    var a = new Float32Array(n);
    var i, p;
    for (i = 0, p = 0; i < px.length; i += 4, p++) a[p] = px[i + 3] / 255;

    /* ------------------------------------------------ 1. le portillon (QUOI)
       Tout se décide sur une copie réduite et durcie : on ne touche pas encore
       à `a`. */
    var s = Math.min(1, COMPO / Math.max(w, h));
    var pw = Math.max(1, Math.round(w * s));
    var ph = Math.max(1, Math.round(h * s));
    var pn = pw * ph;
    var pa = new Float32Array(pn);
    var py, pxi, sy, sx;
    for (py = 0; py < ph; py++) {
      sy = Math.min(h - 1, Math.round(py / s));
      for (pxi = 0; pxi < pw; pxi++) {
        sx = Math.min(w - 1, Math.round(pxi / s));
        pa[py * pw + pxi] = a[sy * w + sx];
      }
    }

    /* 1a. l'incertain n'est gardé qu'au bord du certain. Là où l'IA se trompe
           vraiment — le buisson collé aux jambes, le pan de mur derrière les
           cheveux — son alpha descend nettement sous 1, en larges plaques.
           Une mèche est incertaine elle aussi, mais elle borde toujours un
           sujet franc : on garde l'incertain à quelques pixels d'une zone
           franche, on jette le reste. */
    var rayonDoute = options.doute === undefined ? (options.trait ? 0 : 0.03) : options.doute;
    if (rayonDoute > 0) {
      var noyau = new Uint8Array(pn);
      var nNoyau = 0, nVu = 0;
      for (p = 0; p < pn; p++) {
        if (pa[p] >= 0.88) { noyau[p] = 1; nNoyau++; }
        if (pa[p] >= 0.35) nVu++;
      }
      /* garde-fou : si l'IA n'est franche nulle part (photo difficile), on ne
         touche à rien plutôt que de tout effacer */
      if (nNoyau > 64 && nVu > 0 && nNoyau / nVu > 0.25) {
        var dist = distanceAuNoyau(noyau, pw, ph);
        var seuilD = rayonDoute * Math.max(pw, ph);
        for (p = 0; p < pn; p++) {
          if (!noyau[p] && dist[p] > seuilD) pa[p] = 0;
        }
      }
    }

    /* 1b. durcissement + ouverture, sur la copie réduite seulement : c'est ce
           qui détache les filaments de décor du sujet pour que le tri des
           îlots puisse les emporter. Le tracé fin du sujet, lui, ne risque
           rien : on ne travaille pas sur l'alpha final. */
    /* `trait` : un dessin au trait passé par l'IA faute de fond uni (logo
       photographié sur un mur, capture d'écran sur un dégradé). Ni ouverture
       ni tri sévère : ses lettres SONT des îlots minuscules et ses pleins font
       deux pixels de large. Les raboter revient à effacer le logo. */
    var trait = !!options.trait;
    for (p = 0; p < pn; p++) pa[p] = lisser(trait ? 0.16 : 0.30, trait ? 0.60 : 0.72, pa[p]);
    var rayon = options.ouverture === undefined ? (trait ? 0 : 1) : options.ouverture;
    if (rayon > 0) {
      morpho(pa, pw, ph, rayon, true);
      morpho(pa, pw, ph, rayon, false);
    }

    /* 1c. tri des îlots : les miettes de décor tombent, une famille de vingt
           silhouettes est conservée. */
    var ilots = carteIlots(pa, pw, ph);
    var total = 0;
    ilots.aires.forEach(function (x) { total += x; });
    var seuil = trait ? 4 : Math.max(total * 0.006, 12);
    var garde = ilots.aires.map(function (x) { return x >= seuil; });
    var retires = garde.filter(function (g) { return !g; }).length;

    var conserve = new Float32Array(pn);
    for (p = 0; p < pn; p++) {
      var lab = ilots.label[p];
      conserve[p] = (lab >= 0 && garde[lab]) ? 1 : 0;
    }
    /* Le portillon est ÉLARGI de plusieurs pixels réduits avant d'être
       appliqué : il ne doit dire que « cette région-là n'est pas le sujet »,
       jamais rogner le contour. C'est le filtre guidé qui tracera le bord.
       Sans cette marge, on retomberait dans la découpe aux ciseaux. */
    morpho(conserve, pw, ph, 3, false);
    conserve = flouBoite(conserve, pw, ph, 2);

    /* rééchantillonnage doux du portillon à la taille de travail */
    var mc = document.createElement('canvas');
    mc.width = pw; mc.height = ph;
    var mctx = mc.getContext('2d');
    var md = mctx.createImageData(pw, ph);
    for (p = 0; p < pn; p++) {
      var v = Math.round(255 * Math.max(0, Math.min(1, conserve[p])));
      md.data[p * 4] = md.data[p * 4 + 1] = md.data[p * 4 + 2] = v;
      md.data[p * 4 + 3] = 255;
    }
    mctx.putImageData(md, 0, 0);
    var gc = document.createElement('canvas');
    gc.width = w; gc.height = h;
    var gctx = gc.getContext('2d', { willReadFrequently: true });
    gctx.imageSmoothingEnabled = true;
    gctx.imageSmoothingQuality = 'high';
    gctx.drawImage(mc, 0, 0, w, h);
    var portillon = data(gc).data;

    /* ------------------------------------------------- 2. le tracé (OÙ)
       « Plume guidée » : durcir D'ABORD, guider ENSUITE.

       L'ordre n'est pas un détail, il décide de tout. Guider l'alpha brut
       revient à donner au filtre une bouillie de valeurs moyennes autour du
       sujet : il en conclut que le mur gris derrière les cheveux appartient à
       moitié au sujet, et le recrache en halo gris opaque — essayé, c'était
       pire que l'ancien traitement.

       Durci d'abord, le masque devient franc mais grossier ; le filtre guidé
       n'a plus qu'à recoller ce bord franc sur les contours réels de la photo,
       et les mèches ressortent une à une. Un dernier durcissement efface le
       voile d'interpolation que le filtre laisse dans le décor.

       eps très petit (1e-6) : c'est ce qui autorise le filtre à suivre des
       écarts de luminance minuscules — un cheveu clair sur un mur clair. */
    var guide = null;
    if (options.guide !== false) {
      try {
        guide = luminance(options.guide || image, w, h);
      } catch (e) { guide = null; }
    }
    for (p = 0; p < n; p++) a[p] = lisser(0.30, 0.72, a[p]);
    if (guide) {
      var r = Math.max(2, Math.round((options.finesse === undefined ? 0.020 : options.finesse)
                                     * Math.max(w, h)));
      var eps = options.eps === undefined ? 1e-6 : options.eps;
      a = filtreGuide(guide, a, w, h, r, eps);
      for (p = 0; p < n; p++) a[p] = lisser(0.30, 0.70, a[p]);
    }

    for (p = 0; p < n; p++) a[p] *= portillon[p * 4] / 255;

    /* 3. plume légère : un bord net au pixel près « découpe aux ciseaux » */
    var doux = flouBoite(a, w, h, 1);
    for (p = 0; p < n; p++) {
      var val = doux[p];
      px[p * 4 + 3] = Math.round(255 * (val < 0 ? 0 : val > 1 ? 1 : val));
    }
    c.getContext('2d').putImageData(d, 0, 0);

    var res = recadrer(c, 0.02);
    res.ilotsRetires = retires;
    return res;
  }

  /* Recadre sur le sujet : sans cela, un sujet détouré au milieu d'une grande
     photo arrivait minuscule sur le produit (l'essentiel du cadre était vide). */
  function recadrer(canvas, marge) {
    var d = data(canvas), px = d.data, w = canvas.width, h = canvas.height;
    var minX = w, minY = h, maxX = -1, maxY = -1, x, y, i;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        i = (y * w + x) * 4 + 3;
        if (px[i] < 26) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) return canvas; /* rien d'opaque : on ne touche à rien */
    var m = Math.round(Math.max(w, h) * (marge || 0.02));
    minX = Math.max(0, minX - m); minY = Math.max(0, minY - m);
    maxX = Math.min(w - 1, maxX + m); maxY = Math.min(h - 1, maxY + m);
    var nw = maxX - minX + 1, nh = maxY - minY + 1;
    if (nw >= w * 0.98 && nh >= h * 0.98) return canvas;
    var out = document.createElement('canvas');
    out.width = nw; out.height = nh;
    out.getContext('2d').drawImage(canvas, minX, minY, nw, nh, 0, 0, nw, nh);
    return out;
  }

  /* --------------------------------------------- zone gravable du produit */

  /* Où poser la gravure sur la photo du produit ? Jusqu'ici la photo du client
     était simplement centrée à 50 % du canevas : une photo panoramique sortait
     de la planche, une photo portrait flottait au milieu d'un grand vide.

     On mesure donc le produit lui-même. Le corps de l'article est la bande de
     lignes les plus larges — ce qui écarte d'office la poignée de la planche
     et les pieds d'une bûche — puis on rentre d'une marge : une gravure ne se
     pose jamais au ras du bord.

     Renvoie des fractions du canevas : { x, y, w, h }. */
  function zoneGravable(canvasProduit, options) {
    options = options || {};
    var margeH = options.margeH === undefined ? 0.17 : options.margeH;
    var margeV = options.margeV === undefined ? 0.13 : options.margeV;
    var repli = { x: 0.30, y: 0.28, w: 0.40, h: 0.44 };
    try {
      var petit = toCanvas(canvasProduit, 220);
      var d = data(petit), px = d.data, w = petit.width, h = petit.height;
      var gauche = new Int16Array(h).fill(-1);
      var droite = new Int16Array(h).fill(-1);
      var largeur = new Int16Array(h);
      var x, y, i, maxL = 0;

      /* Référence de fond prise aux quatre coins : l'aperçu n'est pas posé sur
         du blanc pur mais sur le crème de la marque, et le packshot apporte en
         plus son propre fond studio. Sans cette référence, tout le canevas
         passait pour « le produit » et la gravure débordait de la planche. */
      var coins = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + w - 1) * 4];
      var fR = 0, fV = 0, fB = 0;
      coins.forEach(function (c) { fR += px[c]; fV += px[c + 1]; fB += px[c + 2]; });
      fR /= 4; fV /= 4; fB /= 4;

      for (y = 0; y < h; y++) {
        for (x = 0; x < w; x++) {
          i = (y * w + x) * 4;
          var r = px[i], v = px[i + 1], b = px[i + 2];
          var mx = r > v ? (r > b ? r : b) : (v > b ? v : b);
          var mn = r < v ? (r < b ? r : b) : (v < b ? v : b);
          var loin = (Math.abs(r - fR) + Math.abs(v - fV) + Math.abs(b - fB)) / 3 > 24;
          /* un fond de studio reste clair et désaturé, quelle que soit sa teinte */
          var studio = mn > 224 && mx - mn < 24;
          var estProduit = px[i + 3] > 40 && loin && !studio;
          if (!estProduit) continue;
          if (gauche[y] < 0) gauche[y] = x;
          droite[y] = x;
        }
        largeur[y] = gauche[y] < 0 ? 0 : droite[y] - gauche[y] + 1;
        if (largeur[y] > maxL) maxL = largeur[y];
      }
      if (maxL < w * 0.15) return repli;

      /* plus longue bande continue de lignes « larges » = le corps du produit */
      var seuil = maxL * 0.80;
      var meilleurA = -1, meilleurB = -1, a = -1;
      for (y = 0; y < h; y++) {
        if (largeur[y] >= seuil) {
          if (a < 0) a = y;
          if (meilleurA < 0 || y - a > meilleurB - meilleurA) { meilleurA = a; meilleurB = y; }
        } else {
          a = -1;
        }
      }
      if (meilleurA < 0 || meilleurB - meilleurA < h * 0.08) return repli;

      /* bords médians du corps : insensible à une anse ou à un reflet */
      var gs = [], ds = [];
      for (y = meilleurA; y <= meilleurB; y++) {
        if (largeur[y] <= 0) continue;
        gs.push(gauche[y]); ds.push(droite[y]);
      }
      if (!gs.length) return repli;
      gs.sort(function (p, q) { return p - q; });
      ds.sort(function (p, q) { return p - q; });
      var xG = gs[Math.floor(gs.length / 2)];
      var xD = ds[Math.floor(ds.length / 2)];
      var bw = xD - xG, bh = meilleurB - meilleurA;
      if (bw < 4 || bh < 4) return repli;

      return {
        x: (xG + bw * margeH) / w,
        y: (meilleurA + bh * margeV) / h,
        w: (bw * (1 - margeH * 2)) / w,
        h: (bh * (1 - margeV * 2)) / h
      };
    } catch (e) {
      return repli;
    }
  }

  global.BancoImageAnalysis = {
    zoneGravable: zoneGravable,
    analyser: analyser,
    reglages: reglages,
    extraireEncre: extraireEncre,
    nettoyerMasque: nettoyerMasque,
    noterMasque: noterMasque,
    recadrer: recadrer,
    flouBoite: flouBoite,
    filtreGuide: filtreGuide
  };
})(typeof window !== 'undefined' ? window : this);
